import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import type { Components } from 'react-markdown';
import { sanitizeUrl } from '../utils/sanitizeUrl';

interface MarkdownContentProps {
  children: string;
  className?: string;
  /** Preserve single newlines as hard breaks (useful for prompt text). */
  preserveLineBreaks?: boolean;
}

/** Turn soft newlines into markdown hard breaks without an extra plugin. */
function withHardBreaks(text: string): string {
  return text.replace(/([^\n])\n(?!\n)/g, '$1  \n');
}

const markdownComponents: Components = {
  a: ({ href, children, ...props }) => {
    const safeHref = sanitizeUrl(href || '');
    if (!safeHref) {
      return <span {...props}>{children}</span>;
    }
    return (
      <a
        href={safeHref}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {children}
      </a>
    );
  },
};

const MarkdownContent: React.FC<MarkdownContentProps> = ({
  children,
  className = '',
  preserveLineBreaks = false,
}) => {
  const source = preserveLineBreaks ? withHardBreaks(children) : children;

  return (
    <div className={`markdown-content ${className}`.trim()}>
      <ReactMarkdown
        rehypePlugins={[rehypeHighlight]}
        urlTransform={sanitizeUrl}
        skipHtml
        components={markdownComponents}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownContent;
